import { signal, type Signal } from '@angular/core';

/**
 * Tracks stable IDs for placeholder and newly-promoted rows in a reference-row
 * section (teams, locations, skills, employment periods, etc.).
 *
 * Problem solved: calling `Date.now()` inside a `computed()` returns a new
 * value on every re-evaluation, so Angular's `@for (track item.id)` treats
 * the placeholder as a brand-new element and destroys/recreates its DOM node
 * whenever any tracked signal changes (e.g. the user types in a date field).
 *
 * Pattern:
 * ```ts
 * private readonly rowTracker = createSectionRowTracker();
 *
 * // Inside computed():
 * id: this.rowTracker.placeholderId()
 *
 * // When promoting placeholder → real row:
 * id: this.rowTracker.nextItemId()
 * // …after the assignments signal update:
 * this.rowTracker.bumpPlaceholder();
 * ```
 */
export interface SectionRowTracker {
  /** Read inside a `computed()` to opt-in to version tracking. */
  readonly placeholderVersion: Signal<number>;
  /** Stable placeholder ID — safe to call inside `computed()`. */
  placeholderId(): string;
  /** Monotonically increasing ID for a newly promoted row. */
  nextItemId(): string;
  /** Increment version so the next `computed()` evaluation generates a fresh placeholder. */
  bumpPlaceholder(): void;
}

export function createSectionRowTracker(): SectionRowTracker {
  const version = signal(0);
  let counter = 0;
  return {
    placeholderVersion: version,
    placeholderId: () => `placeholder-${version()}`,
    nextItemId: () => `new-${++counter}`,
    bumpPlaceholder: () => version.update(v => v + 1),
  };
}
